import { attributesToProps } from 'html-react-parser';
import { v4 as uuidv4 } from 'uuid';
import type { ParserProps } from '@/app_lib/htmlParser/types';
import { cn } from '@/app_lib/utils/utils';
import { renderChild } from './parserOptions';

/**
 * Recursively processes DOM node children to replace [ID] placeholders with actual unique IDs.
 * This modifies the DOM nodes before they are converted to React elements.
 */
function replaceIdPlaceholders(children: any[], uniqueId: string): any[] {
  return children.map((child) => {
    if (!child || typeof child !== 'object' || !child.attribs) {
      return child;
    }

    // Clone the child node and its attributes
    const newChild = { ...child, attribs: { ...child.attribs } };

    // Replace [ID] placeholders in relevant attributes
    const attributesToProcess = ['id', 'for', 'name', 'aria-labelledby', 'aria-controls'];
    attributesToProcess.forEach((attr) => {
      if (newChild.attribs[attr] && newChild.attribs[attr].includes('[ID]')) {
        newChild.attribs[attr] = newChild.attribs[attr].replace(/\[ID\]/g, uniqueId);
      }
    });

    // Recursively process children
    if (child.children && Array.isArray(child.children)) {
      newChild.children = replaceIdPlaceholders(child.children, uniqueId);
    }

    return newChild;
  });
}

export const SectionParser = ({ domNode }: ParserProps) => {
  const { attribs, children } = domNode;
  const props = attributesToProps(attribs);
  const { className, ...rest } = props;

  const isCardAccordion = className?.toString()?.includes('card-accordion');

  // For card-accordion sections, replace [ID] placeholders with unique UUIDs
  if (isCardAccordion) {
    const uniqueId = uuidv4();
    const processedChildren = replaceIdPlaceholders(children, uniqueId);

    return (
      <section
        className={cn(className)}
        {...rest}
      >
        {renderChild(processedChildren)}
      </section>
    );
  }

  // For other sections, render normally
  return (
    <section
      className={cn(className)}
      {...rest}
    >
      {renderChild(children)}
    </section>
  );
};
